/**
 * «Контроль → Доставка»: заказы Достависты — куда везли, к какой сделке,
 * каким списанием по карте оплачены — и сколько с клиента взяли за доставку
 * против того, сколько она стоила.
 *
 * Спека: `docs/specs/2026-10-02-dostavista-design.md`.
 */
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { CONTROL, STATEMENTS, useCan } from "@/lib/access";
import {
  dealUrl,
  type DeliveryDeal,
  type DeliveryKind,
  type DeliveryOrder,
  type DeliveryReport,
  type DeliverySyncState,
  KIND_LABEL,
  loadDelivery,
  loadDeliverySync,
  MATCH_HINT,
  MATCH_LABEL,
  orderUrl,
  startDeliverySync,
} from "@/lib/delivery";
import { money } from "@/lib/pnl";

const TH = "whitespace-nowrap border-b border-border px-2.5 py-2 text-right align-bottom";
const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const NAME = "border-b border-border px-3 py-1 text-left";
const EDGE = "border-l border-border";
const LINK = "underline decoration-border underline-offset-2 hover:decoration-fg";

const MONTHS = ["", "янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

function day(value: string | null): string {
  return value ? value.slice(0, 10).split("-").reverse().join(".") : "—";
}

function month(value: string): string {
  const [y, m] = value.split("-");
  return `${MONTHS[Number(m)]} ${y}`;
}

function time(iso: string | null): string {
  return iso ? iso.replace("T", " ").slice(0, 16) : "—";
}

/** Разница «взяли − потратили»: минус — доставка обошлась дороже, чем взяли. */
function Diff({ value }: { value: string }) {
  const n = Number(value);
  return <span className={n < 0 ? "text-err" : n > 0 ? "text-ok" : "text-fg-faint"}>{n ? money(value, true) : "—"}</span>;
}

function Num({ value, round = true }: { value: string | number; round?: boolean }) {
  return Number(value) ? <>{money(String(value), round)}</> : <span className="text-fg-faint">—</span>;
}

type Tab = "deals" | "orders";
type DealShow = "all" | "minus" | "free";
type OrderShow = "all" | "no_deal" | "no_bank";

const DEAL_SHOWS: { code: DealShow; label: string; hint: string }[] = [
  { code: "all", label: "все", hint: "" },
  { code: "minus", label: "в минус", hint: "взяли с клиента меньше, чем стоила доставка" },
  { code: "free", label: "ничего не взяли", hint: "в договорах сделки нет услуги «Доставка»" },
];

const ORDER_SHOWS: { code: OrderShow; label: string; hint: string }[] = [
  { code: "all", label: "все", hint: "" },
  { code: "no_deal", label: "без сделки", hint: "с деньгами, а сделка не нашлась ни по заметке, ни по телефону" },
  { code: "no_bank", label: "без списания", hint: "с деньгами, а списания той же суммы по карте рядом по дню нет" },
];

export function DeliveryPage() {
  return useCan(CONTROL) ? <DeliveryScreen /> : <NoAccess what="контролю" />;
}

function DeliveryScreen() {
  const [params, setParams] = useSearchParams();
  const [report, setReport] = useState<DeliveryReport | null>(null);
  const [failure, setFailure] = useState("");
  const [sync, setSync] = useState<DeliverySyncState | null>(null);
  const canSync = useCan(STATEMENTS);

  const year = new Date().getFullYear();
  const from = params.get("date_from") ?? `${year}-01-01`;
  const to = params.get("date_to") ?? `${year}-12-31`;
  const tab = (params.get("tab") as Tab | null) ?? "deals";
  const dealShow = (params.get("deals") as DealShow | null) ?? "all";
  const orderShow = (params.get("orders") as OrderShow | null) ?? "all";
  const monthFilter = params.get("month") ?? "";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const reload = useCallback(() => {
    setFailure("");
    loadDelivery({ date_from: from, date_to: to })
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [from, to]);

  useEffect(() => {
    setReport(null);
    reload();
  }, [reload]);

  useEffect(() => {
    loadDeliverySync()
      .then(setSync)
      .catch(() => undefined);
  }, []);

  const running = sync?.running ?? false;
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      loadDeliverySync()
        .then((state) => {
          setSync(state);
          if (!state.running) reload();
        })
        .catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [running, reload]);

  const launch = () => {
    setFailure("");
    startDeliverySync()
      .then(setSync)
      .catch((error: Error) => setFailure(error.message));
  };

  const inMonth = (iso: string) => !monthFilter || iso.startsWith(monthFilter);
  const deals = (report?.deals ?? []).filter(
    (d) =>
      dealShow === "all" ||
      (dealShow === "minus" ? Number(d.diff) < 0 : Number(d.charged) === 0),
  );
  const orders = (report?.orders ?? []).filter(
    (o) =>
      inMonth(o.date) &&
      (orderShow === "all" ||
        (Number(o.spent) > 0 && (orderShow === "no_deal" ? o.deal_crm_id === null : o.bank_operation_id === null))),
  );

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-fg-muted">
        <DateRangePicker label="период" from={from} to={to} onChange={(f, t) => setParams(withPeriod(params, f, t))} />
        <span>
          Заказы Достависты из кабинета. Сделка — по номеру в заметке («заказ *1245568*»), привязке CRM или телефону
          клиента; «взяли» — услуга «Доставка» в договорах сделки; «по карте» — списания «Достависта» в выписках
          минус возвраты.
        </span>
      </div>

      {canSync ? (
        <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border px-3 py-2 text-[12.5px]">
          <button
            type="button"
            disabled={running}
            onClick={launch}
            className="flex h-8 items-center gap-2 rounded-[var(--radius-ui)] bg-fg px-3 text-[12.5px] font-medium text-bg disabled:opacity-50"
          >
            <RefreshCw size={13} className={running ? "animate-spin" : ""} />
            {running ? "Обновляю из Достависты…" : "Обновить из Достависты"}
          </button>
          <SyncSummary sync={sync} />
        </div>
      ) : null}
      {sync?.report?.error ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          Обновление не удалось: {sync.report.error}
        </div>
      ) : null}
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {report === null ? (
        failure ? null : <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.orders.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">За период заказов Достависты нет.</div>
      ) : (
        <>
          <Months
            report={report}
            selected={monthFilter}
            onMonth={(m) => {
              const next = new URLSearchParams(params);
              if (m) {
                next.set("month", m);
                next.set("tab", "orders");
              } else next.delete("month");
              setParams(next, { replace: true });
            }}
          />

          <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
            {(["deals", "orders"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setParam("tab", t === "deals" ? "" : t)}
                className={`rounded-[var(--radius-ui)] border px-2.5 py-1 font-medium ${
                  tab === t ? "border-fg bg-bg-hover text-fg" : "border-border text-fg-muted hover:bg-bg-hover"
                }`}
              >
                {t === "deals" ? `Сделки · ${report.deals.length}` : `Заказы · ${report.orders.length}`}
              </button>
            ))}
            <span className="mx-1 text-fg-faint">|</span>
            {(tab === "deals" ? DEAL_SHOWS : ORDER_SHOWS).map((s) => {
              const current = tab === "deals" ? dealShow : orderShow;
              return (
                <button
                  key={s.code}
                  type="button"
                  title={s.hint}
                  onClick={() => setParam(tab, s.code === "all" ? "" : s.code)}
                  className={`rounded-[var(--radius-ui)] border px-2 py-1 ${
                    current === s.code ? "border-fg bg-bg-hover text-fg" : "border-border text-fg-muted hover:bg-bg-hover"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
            {monthFilter && tab === "orders" ? (
              <button
                type="button"
                onClick={() => setParam("month", "")}
                className="rounded-[var(--radius-ui)] border border-border px-2 py-1 text-fg-muted hover:bg-bg-hover"
                title="Снять отбор месяца"
              >
                {month(monthFilter)} ×
              </button>
            ) : null}
            <span className="ml-1 text-fg-muted">{tab === "deals" ? deals.length : orders.length} шт.</span>
          </div>

          {tab === "deals" ? <Deals deals={deals} /> : <Orders orders={orders} />}
        </>
      )}
    </main>
  );
}

function SyncSummary({ sync }: { sync: DeliverySyncState | null }) {
  const report = sync?.report;
  if (!report) return <span className="text-fg-muted">Обновлений ещё не было с запуска сервиса.</span>;
  if (sync?.running)
    return (
      <span className="text-fg-muted">
        идёт с {time(report.started)}, страница {report.pages || "…"}
        {report.total_pages ? ` из ${report.total_pages}` : ""}…
      </span>
    );
  const o = report.orders;
  const d = report.deals;
  return (
    <span className="text-fg-muted">
      {time(report.finished)} · заказов новых {o.new ?? 0}, обновлено {o.updated ?? 0} · сделка по заметке {d.note ?? 0},
      CRM {d.crm ?? 0}, телефону {d.phone ?? 0}, без сделки {d.none ?? 0} · списаний связано {report.bank.linked ?? 0}
    </span>
  );
}

function Months({
  report,
  selected,
  onMonth,
}: {
  report: DeliveryReport;
  selected: string;
  onMonth: (month: string) => void;
}) {
  const t = report.totals;
  return (
    <section data-annot="по месяцам" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`}>Месяц</th>
            <th className={TH}>заказов</th>
            <th className={TH} title="Выполненные и удержания за отмену — по кабинету">
              потратили
            </th>
            <th className={`${TH} ${EDGE}`} title="Потратили на заказы, у которых нашлась сделка">
              по сделкам
            </th>
            <th className={TH} title="Услуга «Доставка» в договорах этих сделок; сделка — в месяце первого заказа">
              взяли
            </th>
            <th className={TH} title="Взяли − потратили по сделкам">
              разница
            </th>
            <th className={`${TH} ${EDGE}`} title="Заказы с деньгами, у которых сделка не нашлась">
              без сделки
            </th>
            <th className={`${TH} ${EDGE}`} title="Списания «Достависта» в выписках минус возвраты">
              по карте
            </th>
            <th className={TH} title="Заказов, у которых нашлось своё списание по карте">
              связано
            </th>
          </tr>
        </thead>
        <tbody>
          {report.months.map((m) => (
            <tr
              key={m.month}
              title="Показать заказы месяца"
              onClick={() => onMonth(selected === m.month ? "" : m.month)}
              className={`cursor-pointer hover:bg-bg-hover ${selected === m.month ? "bg-bg-hover" : ""}`}
            >
              <td className={`${NAME} whitespace-nowrap font-medium`}>{month(m.month)}</td>
              <td className={CELL}>{m.orders}</td>
              <td className={CELL}>
                <Num value={m.spent} />
              </td>
              <td className={`${CELL} ${EDGE}`}>
                <Num value={m.spent_with_deal} />
              </td>
              <td className={CELL}>
                <Num value={m.charged} />
              </td>
              <td className={CELL}>
                <Diff value={m.diff} />
              </td>
              <td className={`${CELL} ${EDGE}`}>
                {m.without_deal ? (
                  <span className="text-warn">
                    <span className="text-fg-muted">{m.without_deal} · </span>
                    {money(m.spent_without_deal, true)}
                  </span>
                ) : (
                  <span className="text-fg-faint">—</span>
                )}
              </td>
              <td className={`${CELL} ${EDGE}`}>
                <Num value={m.bank_net} />
              </td>
              <td className={CELL}>{m.linked || <span className="text-fg-faint">—</span>}</td>
            </tr>
          ))}
          <tr className="bg-bg-subtle font-medium">
            <td className={NAME}>Итого</td>
            <td className={CELL}>{t.orders}</td>
            <td className={CELL}>
              <Num value={t.spent} />
            </td>
            <td className={`${CELL} ${EDGE}`}>
              <Num value={t.spent_with_deal} />
            </td>
            <td className={CELL}>
              <Num value={t.charged} />
            </td>
            <td className={CELL}>
              <Diff value={t.diff} />
            </td>
            <td className={`${CELL} ${EDGE}`}>
              {t.without_deal ? `${t.without_deal} · ${money(t.spent_without_deal, true)}` : "—"}
            </td>
            <td className={`${CELL} ${EDGE}`}>
              <Num value={t.bank_net} />
            </td>
            <td className={CELL}>{t.linked}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function Deals({ deals }: { deals: DeliveryDeal[] }) {
  return (
    <section data-annot="сделки" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`}>сделка</th>
            <th className={`${NAME} align-bottom`}>клиент</th>
            <th className={`${NAME} align-bottom`}>город</th>
            <th className={`${NAME} align-bottom`}>выдача → возврат</th>
            <th className={TH}>к клиенту</th>
            <th className={TH}>от клиента</th>
            <th className={`${TH} ${EDGE}`}>потратили</th>
            <th className={TH}>взяли</th>
            <th className={TH}>разница</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => (
            <tr key={d.deal_crm_id}>
              <td className={`${NAME} tabular-nums`}>
                <a href={dealUrl(d.deal_crm_id)} target="_blank" rel="noreferrer" className={LINK}>
                  {d.deal_crm_id}
                </a>
              </td>
              <td className={NAME}>{d.client_name || "—"}</td>
              <td className={`${NAME} whitespace-nowrap`}>{d.city || "—"}</td>
              <td className={`${NAME} whitespace-nowrap tabular-nums`}>
                {day(d.rent_start)} → {day(d.rent_end)}
              </td>
              <td className={CELL}>{d.to_client || <span className="text-fg-faint">—</span>}</td>
              <td className={CELL}>{d.from_client || <span className="text-fg-faint">—</span>}</td>
              <td className={`${CELL} ${EDGE}`}>
                <Num value={d.spent} round={false} />
              </td>
              <td className={CELL} title={d.contracts ? `договоров: ${d.contracts}` : "договора нет"}>
                <Num value={d.charged} round={false} />
              </td>
              <td className={CELL}>
                <Diff value={d.diff} />
              </td>
            </tr>
          ))}
          {deals.length === 0 ? (
            <tr>
              <td colSpan={9} className={`${NAME} py-3 text-fg-muted`}>
                Под отбор ничего не попало.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}

const KIND_TONE: Record<DeliveryKind, string> = {
  to_client: "text-fg",
  from_client: "text-fg-muted",
  office: "text-fg-muted",
  other: "text-warn",
};

function Orders({ orders }: { orders: DeliveryOrder[] }) {
  return (
    <section data-annot="заказы" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`}>заказ</th>
            <th className={`${NAME} align-bottom`}>вид</th>
            <th className={`${NAME} align-bottom`}>откуда → куда</th>
            <th className={`${NAME} align-bottom`}>телефон</th>
            <th className={TH}>сумма</th>
            <th className={`${NAME} ${EDGE} align-bottom`}>сделка</th>
            <th className={`${NAME} align-bottom`}>по карте</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="align-top">
              <td className={`${NAME} whitespace-nowrap tabular-nums`}>
                <a href={orderUrl(o.number)} target="_blank" rel="noreferrer" className={LINK}>
                  {o.number}
                </a>
                <div className="text-[11px] text-fg-muted">{time(o.date)}</div>
              </td>
              <td className={`${NAME} whitespace-nowrap ${KIND_TONE[o.kind]}`}>{KIND_LABEL[o.kind]}</td>
              <td className={NAME} title={o.note || undefined}>
                <div>{o.address_from}</div>
                <div className="text-fg-muted">→ {o.address_to}</div>
                {o.note ? <div className="max-w-[520px] truncate text-[11px] text-fg-faint">{o.note}</div> : null}
              </td>
              <td className={`${NAME} whitespace-nowrap tabular-nums text-fg-muted`}>{o.phone || "—"}</td>
              <td className={CELL}>
                {o.status === "completed" ? (
                  money(o.amount)
                ) : (
                  <span className="text-fg-muted" title={`статус «${o.status}»`}>
                    {Number(o.spent) ? money(o.spent) : "0"} · {o.status === "canceled" ? "отменён" : o.status}
                  </span>
                )}
              </td>
              <td className={`${NAME} ${EDGE} whitespace-nowrap`}>
                {o.deal_crm_id ? (
                  <>
                    <a href={dealUrl(o.deal_crm_id)} target="_blank" rel="noreferrer" className={`${LINK} tabular-nums`}>
                      {o.deal_crm_id}
                    </a>
                    <div className="text-[11px] text-fg-muted" title={MATCH_HINT[o.deal_match]}>
                      {MATCH_LABEL[o.deal_match]}
                    </div>
                  </>
                ) : Number(o.spent) ? (
                  <span className="text-warn">не нашлась</span>
                ) : (
                  <span className="text-fg-faint">—</span>
                )}
              </td>
              <td className={`${NAME} whitespace-nowrap tabular-nums`}>
                {o.bank_date ? day(o.bank_date) : Number(o.spent) ? <span className="text-fg-muted">нет</span> : "—"}
              </td>
            </tr>
          ))}
          {orders.length === 0 ? (
            <tr>
              <td colSpan={7} className={`${NAME} py-3 text-fg-muted`}>
                Под отбор ничего не попало.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
